/**
 * Game core export - entry point for all game logic
 */

import { GameCore } from '@app-games/core';
import { createInitialState } from './state.js';
import { actionHandlers } from './actions.js';
import type { GameState } from './state.js';
import type { GameAction } from './actions.js';

export type { GameState, ActiveBoost, GoldenEvent, ProducerOwned } from './state.js';
export type { PrestigeUpgradeId, Prestige2UpgradeId, AchievementStatus } from './state.js';
export { createInitialState } from './state.js';
export type { GameAction } from './actions.js';
export { actionHandlers } from './actions.js';

// Re-export utilities
export { getTotalCps, getTapPower, getComboTimeout, getCritChance, getCritMultiplier, calculateGcEarned, canPrestige, calculateCoresEarned, canPrestige2, getStartingIp, getOfflineCapHours, getOfflineEfficiency, calculateOfflineIp, getProducerCost } from './actions.js';

// Data exports
export { PRODUCERS, getMilestoneMultiplier, MILESTONES, getProducerById } from './data/producers.js';
export type { ProducerDef, Milestone } from './data/producers.js';
export { CLICK_UPGRADES, SPECIAL_UPGRADES } from './data/upgrades.js';
export type { ClickUpgradeDef, SpecialUpgradeDef } from './data/upgrades.js';
export { PRESTIGE_UPGRADES, getPrestigeUpgradeCost, PRESTIGE2_UPGRADES } from './data/prestige.js';
export type { PrestigeUpgradeDef } from './data/prestige.js';
export { ALL_ACHIEVEMENTS, checkAchievements, totalProducerCount } from './data/achievements.js';
export type { AchievementDef, AchievementCategory } from './data/achievements.js';
export { RANKS, getCurrentRank, getEligibleRank, getNextPromotableRank } from './data/ranks.js';
export type { RankDef } from './data/ranks.js';
export { MISSION_TEMPLATES, generateDailyMissions, getUtcDateString, getMissionTemplate, ALL_COMPLETE_BONUS_DOCS, DAILY_MISSION_COUNT } from './data/missions.js';
export type { MissionTemplate, MissionType, MissionDifficulty, MissionReward, DailyMission } from './data/missions.js';

// Conquest exports
export { COUNTRIES, getFacilityCost } from './data/conquest.js';
export type { CountryDef, FacilityDef } from './data/conquest.js';
export { isCountryUnlocked, isAllConquered, getConqueredCount, getConquestCpsMult, getConquestTapMult, getConquestOfflineBonus, getConquestGcBonus, getConquestGoldenBonus, getConquestProducerDiscount, getInfiltrationPower } from './systems/conquest.js';
export type { ConquestCountryState, ConquestState, BossId, BossEvent, BossState, AgentRarity, AgentCard, AgentState, FactionId, FactionRelation, ActiveFactionEvent, FactionState } from './state.js';

// Boss exports
export { BOSS_DEFS, getBossDef } from './data/boss.js';
export type { BossDef, BossReward, BossRewardType, BossUnlockCondition } from './data/boss.js';
export { calcBossHp, calcTapDamage, calcBossReward, calcNextBossDelay, getAvailableBosses, pickRandomBoss } from './systems/boss.js';

// Agent exports
export { AGENT_DEFS, getAgentDef, RARITY_COLORS } from './data/agents.js';
export type { AgentDef, AgentEffectType, AgentObtainCondition } from './data/agents.js';
export { getEquippedAgentBonus, getEquipSlotCount, rollAgentDrop, grantAgent, getCollectedUniqueCount, isAgentEquipped } from './systems/agents.js';

// Faction exports
export { FACTION_DEFS, FAVORABILITY_TIERS, FACTION_EVENTS, EVENT_INTERVAL_MIN, EVENT_INTERVAL_MAX, EVENT_DURATION, AD_BONUS_FAVOR, getFactionDef, getEventDef } from './data/factions.js';
export type { FactionDef, FactionBonus, FactionEventDef } from './data/factions.js';
export { getAllFactionBonus, calcFavorTier, pickRandomEvent } from './systems/faction.js';

// Infiltration exports
export { INFIL_EVENTS, CAREER_TIERS, TUTORIAL_EVENT_IDS, TUTORIAL_STATS, RUN_EVENT_MIN, RUN_EVENT_MAX, careerNameKey, careerRank, higherCareer, getInfilEvent, getInfilChoice } from './data/infiltration.js';
export type { InfilStatType, InfilTag, InfilChoice, InfilEvent, CareerTier } from './data/infiltration.js';
export { computeSuccessRate, rollSuccess, detectionDelta, clampDetection, isCaught, finalSuccessChance, resolveFinalOutcome, choiceAmbition, pickCareer, computeRunReward, getInfiltrationStage, pickEventCount, pickEventQueue, tutorialForcedSuccess, DETECTION_CATCH, FAIL_COOLDOWN_MS } from './systems/infiltration.js';
export type { InfiltrationStats, InfiltrationRunState, InfiltrationState, InfilChoiceResult } from './state.js';

// Operation mode exports (Plague-style world domination)
export { REGIONS, getRegion, startableRegionIds } from './data/regions.js';
export type { RegionDef, Climate } from './data/regions.js';
export { EVOLUTION_NODES, getEvoNode, nodesForTree, prereqMet, aggregateEvoEffects, raceBaseEffects } from './data/evolution.js';
export type { EvoNode, EvoTree, EvoEffects, OperationRace } from './data/evolution.js';
export { stepInfection, meanInfection, totalInfectionUnits, dominatedCount, allDominated, envMatch, DOMINATED_AT } from './systems/spread.js';
export { stepResistance, computeExposure, resistanceComplete, dnaPerTick, bubbleDnaAmount, difficultyResistMul, RESISTANCE_MAX } from './systems/resistance.js';
export { createOperationRun, advanceOperation, canBuyEvolution, blankOperation, OP_START_INFECTION, OP_START_DNA, BUBBLE_INTERVAL_TICKS } from './systems/operation.js';
export type { OperationState, OperationDifficulty, DnaBubble } from './state.js';

/**
 * Create game core instance
 */
export function createGameCore(debug = false) {
  const core = new GameCore<GameState, GameAction>({
    createInitialState,
    debug,
    historySize: 100,
  });

  // Register action handlers
  for (const [type, handler] of Object.entries(actionHandlers)) {
    core.registerAction(type as GameAction['type'], handler as any);
  }

  return core;
}
